import { Fragment, useEffect, useState } from "react";
import moment from "moment";
import { LayerContent } from "../../fall-analysis/style";
import { useAnalyticsWrapper } from "../../../../../store/store";
import useAnalytics from "../../../../../hooks/useAnalytics";
import {
  doTransformData,
  handlePointClicked,
  transformDataForBarChart,
} from "../../fall-analysis/formUtils";
import CustomerLoader from "../../../../../components/CustomLoader";
import { DefaultContainer } from "../../fall-analysis/sections/SectionModal";
import CaregiverAuditDrilldownModal from "./CaregiverAuditDrilldownModal";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { caregiverAuditDetailCols } from "../../dummyData/colums";
import { Grid, GridColumn, GridColumnProps } from "@progress/kendo-react-grid";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";

const CaregiverAuditSection = ({
  setClickedIncident,
  isFetchingSelectedIncident,
  shareTableData,
}: any) => {
  const [tableData, setTableData] = useState<any>([]);
  const [grid, setGrid] = useState<any>([]);
  const [barData, setBarData] = useState<any>([]);
  const [caregiver, setCaregiver] = useState("");
  const [caregiverId, setCaregiverId] = useState("");
  const [showCaregiverModal, setShowCaregiverModal] = useState(false);
  const [caregiverDetails, setCaregiverDetails] = useState<any>([]);

  const {
    startDate,
    endDate,
    caregiverAuditFacility,
    caregiverAuditIncidentTypes,
    caregiverAuditCaregiverIds,
  } = useAnalyticsWrapper((state) => ({
    startDate: state.startDate,
    endDate: state.endDate,
    caregiverAuditFacility: state.caregiverAuditFacility,
    caregiverAuditIncidentTypes: state.caregiverAuditIncidentTypes,
    caregiverAuditCaregiverIds: state.caregiverAuditCaregiverIds,
  }));

  const incidentTypesCsv = (caregiverAuditIncidentTypes || []).join(",");
  const caregiverIdsCsv = (caregiverAuditCaregiverIds || []).join(",");

  const { getCaregiverAuditOverview, getCaregiverAuditDetails } = useAnalytics({
    facility: caregiverAuditFacility?.value || "",
    startDate: moment(startDate).format("yyyy-MM-DD"),
    endDate: moment(endDate).format("yyyy-MM-DD"),
    createdBy: caregiver,
    createdById: caregiverId,
    incidentTypes: incidentTypesCsv,
    caregiverIds: caregiverIdsCsv,
    queries: ["caregiverAuditOverview", "caregiverAuditDetails"],
  });

  const { data: caregiversData, isFetching: isFetchingCaregivers } =
    getCaregiverAuditOverview;

  const { data: caregiversDetailsData, isFetching: isLoadingDetails } =
    getCaregiverAuditDetails;

  useEffect(() => {
    if (caregiversData !== undefined) {
      // Deep clone to avoid mutating the cached query data.
      const tempData = JSON.parse(JSON.stringify(caregiversData?.data.data));

      // Map the caregiver rows onto the name/id shape the fall transforms expect.
      if (Array.isArray(tempData?.caregiverAudit)) {
        tempData.caregiverAudit = tempData.caregiverAudit.map((item: any) => ({
          residentName: item.caregiverName,
          residentId: item.caregiverId,
          count: item.count,
          percentage: item.percentage,
        }));
      }

      const transformedTableData = doTransformData(
        tempData,
        "caregiverAudit",
        "residentName",
        "Total"
      );
      const transformedBarData = transformDataForBarChart(
        transformedTableData,
        "Incident Count"
      );
      setTableData(transformedTableData);
      shareTableData(transformedTableData);
      setBarData(transformedBarData);
    }
  }, [caregiversData]);

  useEffect(() => {
    if (caregiversDetailsData) {
      const tempDetails = caregiversDetailsData.data.data.map((item: any) => ({
        ...item,
      }));
      setCaregiverDetails(tempDetails);
    }
  }, [caregiversDetailsData]);

  const title = "Incidents by Caregiver (All Roles)";

  const handleBarClicked = (chart: any) => {
    if (!chart.length) return;
    const label = handlePointClicked(chart, barData?.labels);
    // Drill down by the caregiver's Contact Id (compound Name isn't filterable
    // with Middle Name on). Bars aggregate by name, so only trust the Id when
    // exactly one row matches the label; otherwise fall back to name.
    const matchedRows = (tableData ?? []).filter(
      (row: any) => row.residentName === label
    );
    const resolvedId =
      matchedRows.length === 1 ? matchedRows[0].residentId ?? "" : "";
    setShowCaregiverModal(true);
    setCaregiver(label);
    setCaregiverId(resolvedId);
  };

  useEffect(() => {
    let cellCountProps = {
      cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
    };
    let cellPercentageProps = {
      cells: {
        data: CenteredWholeNumCell,
        append: "%",
        headerCell: HeaderCenteredCell,
      },
    };
    const grid = (
      <Fragment>
        <Grid
          data={tableData || []}
          scrollable="none"
          rows={{ data: TotalRow }}
        >
          <GridColumn field="residentName" title="Caregiver" />
          <GridColumn
            field="count"
            title="Number"
            {...(cellCountProps as GridColumnProps)}
          />
          <GridColumn
            field="percentage"
            title="%"
            {...(cellPercentageProps as GridColumnProps)}
          />
        </Grid>
      </Fragment>
    );
    setGrid(grid);
  }, [tableData]);

  return (
    <LayerContent>
      {isFetchingCaregivers ? (
        <CustomerLoader />
      ) : (
        <DefaultContainer
          title={title}
          grid={grid}
          handleBarClicked={(chart: any) => handleBarClicked(chart)}
          barData={barData}
          barType="horizontal"
        />
      )}
      {showCaregiverModal ? (
        <CaregiverAuditDrilldownModal
          open={showCaregiverModal}
          facilityName={caregiverAuditFacility?.label ?? ""}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowCaregiverModal(false)}
          resetFn={() => {
            setCaregiver("");
            setCaregiverId("");
            setCaregiverDetails(undefined);
          }}
          isLoading={isLoadingDetails || isFetchingSelectedIncident}
          tableData={caregiverDetails}
          tableCols={caregiverAuditDetailCols()}
          title={`${title} - ${caregiver}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: caregiverDetails,
              columnData: caregiverAuditDetailCols(),
              fileName: caregiverAuditFacility?.label,
              startDate,
              endDate,
              subTitle: `${title} - ${caregiver}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              caregiverDetails,
              caregiverAuditDetailCols(),
              `${caregiverAuditFacility?.label} - ${title} - ${caregiver}`,
              caregiverAuditFacility?.label,
              startDate,
              endDate
            )
          }
        />
      ) : null}
    </LayerContent>
  );
};

export default CaregiverAuditSection;
